import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaUser, FaCalendarAlt, FaGraduationCap, FaClipboardCheck, FaBook, FaHistory } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import { formatMongoDate } from '../utils/dateUtils.js';  // Import date utilities
import { getActionErrorMessage } from '../utils/errorHandler';
import "../styles/theme.css";
import CollapsibleNavbar from "./CollapsibleNavbar.jsx";
import StudentChoiceForm from "./StudentChoiceForm.jsx";
import ViewStudentSchedule from "./ViewStudentSchedule.jsx";
import StudentAttendanceFormModern from "./StudentAttendanceFormModern.jsx";
import StudentEvaluationForm from "./StudentEvaluationForm.jsx";
import MyAttendancePage from "./MyAttendancePage.jsx";
import MyEvaluationsPage from "./MyEvaluationsPage.jsx";
import MyOrganizationPage from "./MyOrganizationPage.jsx";
import ModifyUser from "./ModifyUser.jsx";
import NonAdminResourceCenter from "./NonAdminResourceCenter.jsx";
import NonAdminReportCenter from "./NonAdminReportCenter.jsx";
import ChangePassword from "./ChangePassword";
import DueEvents from './DueEvents.jsx';
import LoadingSpinner from './LoadingSpinner';
import { normalizeUser } from '../utils/userUtils';

// Views that already render their own full-width page shell (the `mc-*` /
// universal-white-container layout used by Manage Curricula, or a bespoke one).
// Wrapping them in a `content-box` would nest a white card inside a white card.
const SELF_SHELLED_VIEWS = new Set([
    "change-password",
    "my-account",
    "resource-center",
    "my-organization",
    "my-evaluations",
    "my-attendance",
]);

const StudentDashboard = () => {
    const [studentData, setStudentData] = useState(null);
    const [error, setError] = useState("");
    const [currentRotation, setCurrentRotation] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = useParams();

    // Initialize currentView from the URL. `view` comes from the /:view route
    // param; the pathname fallback keeps the page correct if the route is ever
    // matched by a splat pattern, where `view` would be undefined.
    const pathView = location.pathname.split('/').filter(Boolean)[1];
    const currentView = view || pathView || "dashboard";

    // Update URL when view changes
    const handleViewChange = (newView) => {
        if (newView === "dashboard") {
            navigate("/StudentDashboard", { replace: true });
        } else {
            navigate(`/StudentDashboard/${newView}`, { replace: true });
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Wait for token to be available
                let retries = 0;
                while (!localStorage.getItem('access_token') && retries < 10) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    retries++;
                }

                // Verify token
                const token = localStorage.getItem('access_token');
                if (!token) {
                    console.error('No token found after waiting');
                    throw new Error('No authentication token found');
                }

                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔑 Token found, making API calls...');

                // Fetch student data
                const studentResponse = await axiosInstance.get("/auth/student/dashboard");
                if (!studentResponse.data?.data) {
                    throw new Error('Invalid student data received');
                }
                setStudentData(normalizeUser(studentResponse.data.data));
                if (studentResponse.data.data?.status) {
                    localStorage.setItem('student_status', String(studentResponse.data.data.status));
                }

                // Fetch current rotation data
                const rotationResponse = await axiosInstance.get("/crud/student/schedule_of_current_rotation");
                setCurrentRotation(rotationResponse.data);

            } catch (err) {
                console.error('Fetch Error:', {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status
                });

                if (err.code === 'ERR_NETWORK') {
                    setError('Unable to connect to server. Please verify the backend is running.');
                } else if (err.response?.status === 401) {
                    navigate('/');
                } else {
                    setError(getActionErrorMessage(err, 'load student dashboard'));
                }
            }
        };

        fetchData();
    }, [navigate]);

    const renderDashboardContent = () => {
        const studentStatus = String(studentData?.status || studentData?.rank || '').toLowerCase();
        const isAlumni = studentStatus === 'alumni';
        
        return (
            <div className="modern-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">
                        Welcome, {studentData.firstname} {studentData.lastname}
                    </h1>
                    <p className="dashboard-subtitle">Student Dashboard</p>
                </div>

                {/* Alumni Notice Banner */}
                {isAlumni && (
                    <div className="alumni-notice-banner">
                        <div className="alumni-notice-content">
                            <div className="alumni-notice-icon">🎓</div>
                            <div className="alumni-notice-text">
                                <h3>Welcome Back, Alumni! </h3>
                                <p>Great to see you again! As a valued alumni, you have special read-only access to view your academic history, schedules, documents, and generate comprehensive reports of your journey with us.</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="dashboard-grid">
                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaUser style={{ color: '#4a90e2' }} />
                            <h2 className="card-title">Personal Information</h2>
                        </div>
                        <div className="card-content">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Student ID</span>
                                    <span className="info-value">{studentData._id}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Email Address</span>
                                    <span className="info-value">{studentData.email}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Student Rank</span>
                                    <span className={`info-value ${isAlumni ? 'alumni-badge' : 'role-badge'}`}>
                                        {isAlumni ? '🎓 Alumni' : (studentData?.rank || studentStatus || 'N/A')}
                                    </span>
                                </div>
                                {studentData.currentCurriculum && (
                                    <div className="info-item">
                                        <span className="info-label">Active Curriculum</span>
                                        <span className="info-value curriculum-badge">
                                            {studentData.currentCurriculum}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaCalendarAlt style={{ color: '#10b981' }} />
                            <h2 className="card-title">Current Rotation</h2>
                        </div>
                        <div className="card-content">
                            {currentRotation && (currentRotation.rotation_start || currentRotation.rotationStartDate) ? (
                                <>
                                    <div className="rotation-period">
                                        <span className="period-label">Rotation Period:</span>
                                        <span className="period-dates">
                                            {formatMongoDate(currentRotation.rotationStartDate || currentRotation.rotation_start)} 
                                            - {formatMongoDate(currentRotation.rotationEndDate || currentRotation.rotation_end)}
                                        </span>
                                    </div>
                                    {currentRotation?.preceptor ? (
                                        <div className="preceptor-card">
                                            <div className="preceptor-info">
                                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#ffffff', fontSize: '1.1rem' }}>
                                                    Dr. {currentRotation.preceptor.name}
                                                </h4>
                                                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <strong>Specialty:</strong> <span style={{ color: '#ffffff' }}>{currentRotation.speciality}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <strong>Location:</strong> <span style={{ color: '#ffffff' }}>{currentRotation.preceptor.institution}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="no-data-message">
                                            <div className="no-data-icon">👨‍⚕️</div>
                                            <p>No preceptor assigned yet</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="no-data-message">
                                    <div className="no-data-icon">📅</div>
                                    <p>No active rotation period found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Due Events Section - Only show for active students */}
                {!isAlumni && (
                    <DueEvents 
                        userId={studentData._id} 
                        userRole="student" 
                    />
                )}
            </div>
        );
    };

    const renderContent = () => {
        // Check if user is alumni
        const studentStatus = String(studentData?.status || studentData?.rank || '').toLowerCase();
        const isAlumni = studentStatus === 'alumni';
        
        // Alumni restrictions: only allow viewing schedules, resource center, report center, dashboard, and my-account
        // Alumni students can:
        // - View their schedules and rotation history
        // - Access resource center for documents and reports  
        // - Access report center for generating and exporting reports
        // - Export their data and reports
        // - View and update their account information
        // - Change their password
        // Alumni students cannot:
        // - Submit attendance forms
        // - Update rotation choices
        // - Evaluate preceptors
        const allowedViewsForAlumni = ['dashboard', 'schedule', 'resource-center', 'report-center', 'my-account', 'change-password', 'my-attendance', 'my-evaluations'];
        
        if (isAlumni && !allowedViewsForAlumni.includes(currentView)) {
            return (
                <div className="alumni-restriction-message">
                    <div className="restriction-container">
                        <div className="restriction-icon">🎓</div>
                        <h3>Alumni Access Restricted</h3>
                        <p>As an alumni, you have limited access to certain features. You can still access:</p>
                        <ul>
                            <li>📅 View your complete schedule and rotation history</li>
                            <li>📚 Access resource center for documents and templates</li>
                            <li>📊 Generate and export detailed academic reports</li>
                            <li>👤 View and update your personal account information</li>
                            <li>🔒 Change your account password</li>
                        </ul>
                        <p>Active student features like form submissions and evaluations are not available.</p>
                        <button 
                            className="modern-button primary" 
                            onClick={() => handleViewChange('dashboard')}
                        >
                            🏠 Return to Dashboard
                        </button>
                    </div>
                </div>
            );
        }
        
        switch (currentView) {
            case "choices":
                return <StudentChoiceForm />;
            case "schedule":
                return <ViewStudentSchedule />;
            case "forms":
                return <StudentAttendanceFormModern />;
            case "my-attendance":
                return <MyAttendancePage />;
            case "evaluate-preceptor":
                return <StudentEvaluationForm />;
            case "my-evaluations":
                return <MyEvaluationsPage userLabel="My Evaluations" />;
            case "evaluation-center":
                return <MyEvaluationsPage userLabel="My Evaluations" />;
            case "my-organization":
                return <MyOrganizationPage canEdit={false} />;
            case "my-account":
                return <ModifyUser userId={studentData?._id} userType="student" />;
            case "resource-center":
                return <NonAdminResourceCenter userType="student" userData={studentData} />;
            case "report-center":
                return <NonAdminReportCenter userType="student" userData={studentData} />;
            case "change-password":
                return <ChangePassword />;
            case "dashboard":
            default:
                return renderDashboardContent();
        }
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <div className="error-container">
                        <div className="error-icon">⚠️</div>
                        <p className="error-message">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!studentData) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <LoadingSpinner text="Loading student dashboard..." variant="page" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <CollapsibleNavbar
                setCurrentView={handleViewChange}
                userType="student"
                isStandaloneAdmin={false}
                isAdmin={false}
            />
            {SELF_SHELLED_VIEWS.has(currentView) ? (
                renderContent()
            ) : (
                <div className="content-box">
                    {renderContent()}
                </div>
            )}
        </div>
    );
};

export default StudentDashboard;
